// Invoice Calculation Utilities
// Phase 2 - Week 5: Invoice Generation Migration
// Based on existing 7twenty4gen2 invoice system patterns

import { 
  Invoice, 
  InvoiceLineItem, 
  InvoiceGenerationOptions,
  INVOICE_CONSTANTS,
  BusinessInfo
} from '../types/invoice.ts';
import { TimesheetEntry } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';

export class InvoiceCalculator {
  /**
   * Generate invoice from approved timesheets
   */
  static generateInvoiceFromTimesheets(
    timesheets: TimesheetEntry[],
    employees: Employee[],
    options: InvoiceGenerationOptions
  ): Partial<Invoice> {
    if (!timesheets.length) {
      throw new Error('No timesheets provided for invoice generation');
    }

    // Group timesheets by employee and job site
    const groupedData = this.groupTimesheetData(timesheets, employees, options);
    
    // Generate line items
    const lineItems = this.generateLineItems(groupedData, options);
    
    // Calculate totals
    const totals = this.calculateInvoiceTotals(lineItems);
    
    // Determine client information
    const clientInfo = this.determineClientInfo(timesheets, employees);
    
    // Generate invoice number
    const invoiceNumber = this.generateInvoiceNumber(options);

    return {
      invoiceNumber,
      jobId: options.jobId || '',
      jobNumber: this.extractJobNumber(timesheets),
      jobName: this.extractJobName(timesheets),
      clientName: clientInfo.name,
      clientAddress: clientInfo.address,
      clientCity: clientInfo.city,
      clientState: clientInfo.state,
      clientZip: clientInfo.zip,
      invoiceDate: options.invoiceDate,
      dueDate: new Date(options.invoiceDate.getTime() + (INVOICE_CONSTANTS.DEFAULT_PAYMENT_TERMS * 24 * 60 * 60 * 1000)),
      weekEnding: options.weekEnding,
      lineItems,
      ...totals,
      status: 'draft',
      useBtcPrefix: options.useBtcPrefix || false,
      useRelPrefix: options.useRelPrefix || false,
      timesheetIds: timesheets.map(t => t.id),
      taxRate: INVOICE_CONSTANTS.DEFAULT_TAX_RATE,
      taxAmount: 0 // No tax by default
    };
  }

  /**
   * Group timesheet data for invoice generation
   */
  private static groupTimesheetData(
    timesheets: TimesheetEntry[],
    employees: Employee[],
    options: InvoiceGenerationOptions
  ) {
    const grouped: { [key: string]: any } = {};

    timesheets.forEach(timesheet => {
      const employee = employees.find(e => e.id === timesheet.employeeId);
      if (!employee) return;

      const employeeName = timesheet.employeeName;
      const activityKey = timesheet.jobDescription || 'General Work';

      if (!grouped[employeeName]) {
        grouped[employeeName] = {
          employee,
          activities: {},
          expenses: []
        };
      }

      if (!grouped[employeeName].activities[activityKey]) {
        grouped[employeeName].activities[activityKey] = {
          activityCode: '',
          activityDescription: activityKey,
          regularHours: 0,
          overtimeHours: 0,
          regularRate: this.getEmployeeRate(employee, 'regular'),
          overtimeRate: this.getEmployeeRate(employee, 'overtime'),
          regularTotal: 0,
          overtimeTotal: 0
        };
      }

      const activity = grouped[employeeName].activities[activityKey];

      // Add hours based on work type
      if (timesheet.workType === 'regular') {
        activity.regularHours += timesheet.hoursWorked;
      } else if (timesheet.workType === 'overtime') {
        activity.overtimeHours += timesheet.overtimeHours || 0;
        activity.regularHours += (timesheet.hoursWorked - (timesheet.overtimeHours || 0));
      } else if (timesheet.workType === 'double_time') {
        activity.overtimeHours += timesheet.doubleTimeHours || 0;
        activity.regularHours += (timesheet.hoursWorked - (timesheet.doubleTimeHours || 0));
      }

      // Recalculate totals
      activity.regularTotal = parseFloat((activity.regularHours * activity.regularRate).toFixed(2));
      activity.overtimeTotal = parseFloat((activity.overtimeHours * activity.overtimeRate).toFixed(2));
    });

    return grouped;
  }

  /**
   * Generate line items from grouped data
   */
  private static generateLineItems(
    groupedData: { [key: string]: any },
    options: InvoiceGenerationOptions
  ): InvoiceLineItem[] {
    const lineItems: InvoiceLineItem[] = [];

    Object.entries(groupedData).forEach(([employeeName, employeeData]) => {
      Object.entries(employeeData.activities).forEach(([activityDesc, activityData]: [string, any]) => {
        const lineItem: InvoiceLineItem = {
          id: this.generateLineItemId(),
          employeeName,
          activityDescription: activityDesc,
          regularHours: activityData.regularHours,
          overtimeHours: activityData.overtimeHours,
          totalHours: activityData.regularHours + activityData.overtimeHours,
          regularRate: activityData.regularRate,
          overtimeRate: activityData.overtimeRate,
          regularTotal: activityData.regularTotal,
          overtimeTotal: activityData.overtimeTotal,
          lineTotal: activityData.regularTotal + activityData.overtimeTotal,
          isExpense: false
        };

        lineItems.push(lineItem);
      });

      // Add expenses if any
      if (options.includeExpenses && employeeData.expenses?.length > 0) {
        employeeData.expenses.forEach((expense: any) => {
          const expenseItem: InvoiceLineItem = {
            id: this.generateLineItemId(),
            employeeName,
            activityDescription: expense.description,
            regularHours: 0,
            overtimeHours: 0,
            totalHours: 0,
            regularRate: 0,
            overtimeRate: 0,
            regularTotal: 0,
            overtimeTotal: 0,
            lineTotal: expense.amount,
            isExpense: true,
            expenseDescription: expense.description,
            expenseAmount: expense.amount
          };

          lineItems.push(expenseItem);
        });
      }
    });

    return lineItems;
  }

  /**
   * Calculate invoice totals
   */
  static calculateInvoiceTotals(lineItems: InvoiceLineItem[]) {
    let totalRegularHours = 0;
    let totalOvertimeHours = 0;
    let totalAmount = 0;
    let totalExpenses = 0;

    lineItems.forEach(item => {
      if (item.isExpense) {
        totalExpenses += item.expenseAmount || 0;
        totalAmount += item.expenseAmount || 0;
      } else {
        totalRegularHours += item.regularHours;
        totalOvertimeHours += item.overtimeHours;
        totalAmount += item.lineTotal;
      }
    });

    return {
      totalRegularHours: parseFloat(totalRegularHours.toFixed(2)),
      totalOvertimeHours: parseFloat(totalOvertimeHours.toFixed(2)),
      totalHours: parseFloat((totalRegularHours + totalOvertimeHours).toFixed(2)),
      subtotal: parseFloat(totalAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      totalExpenses: parseFloat(totalExpenses.toFixed(2))
    };
  }

  /**
   * Get employee rate based on client and work type
   */
  private static getEmployeeRate(employee: Employee, workType: 'regular' | 'overtime'): number {
    // Default to CEC rates for now - this can be enhanced based on client selection
    const baseRate = employee.cecRate || employee.hourlyWage;
    
    if (workType === 'overtime') {
      return employee.cecOtRate || (baseRate * INVOICE_CONSTANTS.OVERTIME_MULTIPLIER);
    }
    
    return baseRate;
  }

  /**
   * Determine client information from timesheets
   */
  private static determineClientInfo(timesheets: TimesheetEntry[], employees: Employee[]) {
    // For now, default to CEC - this can be enhanced based on job site or other criteria
    return INVOICE_CONSTANTS.CLIENTS.CEC;
  }

  /**
   * Extract job number from timesheets
   */
  private static extractJobNumber(timesheets: TimesheetEntry[]): string {
    // Extract from job site or use a default pattern
    const firstTimesheet = timesheets[0];
    return firstTimesheet.jobSite.split(' ')[0] || 'JOB-001';
  }

  /**
   * Extract job name from timesheets
   */
  private static extractJobName(timesheets: TimesheetEntry[]): string {
    const firstTimesheet = timesheets[0];
    return firstTimesheet.jobSite || 'General Work';
  }

  /**
   * Generate invoice number
   */
  private static generateInvoiceNumber(options: InvoiceGenerationOptions): string {
    const timestamp = Date.now().toString().slice(-6);
    let baseNumber = `INV-${timestamp}`;

    if (options.useBtcPrefix) {
      baseNumber = `${INVOICE_CONSTANTS.PREFIXES.BTC}${baseNumber}`;
    } else if (options.useRelPrefix) {
      baseNumber = `${INVOICE_CONSTANTS.PREFIXES.REL}${baseNumber}`;
    }

    return baseNumber;
  }

  /**
   * Generate unique line item ID
   */
  private static generateLineItemId(): string {
    return `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  }

  /**
   * Format number with 2 decimal places
   */
  static formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value);
  }

  /**
   * Validate invoice data
   */
  static validateInvoice(invoice: Partial<Invoice>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!invoice.invoiceNumber) {
      errors.push('Invoice number is required');
    }

    if (!invoice.jobNumber) {
      errors.push('Job number is required');
    }

    if (!invoice.jobName) {
      errors.push('Job name is required');
    }

    if (!invoice.clientName) {
      errors.push('Client name is required');
    }

    if (!invoice.invoiceDate) {
      errors.push('Invoice date is required');
    }

    if (!invoice.lineItems || invoice.lineItems.length === 0) {
      errors.push('At least one line item is required');
    }

    if (invoice.totalAmount === undefined || invoice.totalAmount <= 0) {
      errors.push('Total amount must be greater than zero');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get business information
   */
  static getBusinessInfo(): BusinessInfo {
    return {
      name: "Twenty4 Services LLC",
      address: "200 Greenlea St",
      city: "Pulaski",
      state: "VA",
      zip: "24301",
      phone: "(972) 333-0913",
      email: "kim@twenty4services.com",
      paymentTerms: "Net 30",
      logoUrl: "/logo.png"
    };
  }

  /**
   * Calculate due date based on payment terms
   */
  static calculateDueDate(invoiceDate: Date, paymentTerms: number = INVOICE_CONSTANTS.DEFAULT_PAYMENT_TERMS): Date {
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + paymentTerms);
    return dueDate;
  }

  /**
   * Determine invoice status based on dates
   */
  static determineInvoiceStatus(invoice: Invoice): 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' {
    const now = new Date();
    
    if (invoice.status === 'paid' || invoice.status === 'cancelled') {
      return invoice.status;
    }

    if (invoice.status === 'sent' && now > invoice.dueDate) {
      return 'overdue';
    }

    return invoice.status;
  }

  /**
   * Group invoices by client for reporting
   */
  static groupInvoicesByClient(invoices: Invoice[]): { [clientName: string]: Invoice[] } {
    return invoices.reduce((groups, invoice) => {
      const clientName = invoice.clientName;
      if (!groups[clientName]) {
        groups[clientName] = [];
      }
      groups[clientName].push(invoice);
      return groups;
    }, {} as { [clientName: string]: Invoice[] });
  }

  /**
   * Calculate invoice statistics
   */
  static calculateInvoiceStats(invoices: Invoice[]) {
    const stats = {
      totalInvoices: invoices.length,
      draftInvoices: 0,
      sentInvoices: 0,
      paidInvoices: 0,
      overdueInvoices: 0,
      cancelledInvoices: 0,
      totalAmount: 0,
      totalPaid: 0,
      totalOutstanding: 0,
      averageInvoiceAmount: 0,
      totalRegularHours: 0,
      totalOvertimeHours: 0
    };

    invoices.forEach(invoice => {
      const status = this.determineInvoiceStatus(invoice);
      
      switch (status) {
        case 'draft':
          stats.draftInvoices++;
          break;
        case 'sent':
          stats.sentInvoices++;
          stats.totalOutstanding += invoice.totalAmount;
          break;
        case 'paid':
          stats.paidInvoices++;
          stats.totalPaid += invoice.totalAmount;
          break;
        case 'overdue':
          stats.overdueInvoices++;
          stats.totalOutstanding += invoice.totalAmount;
          break;
        case 'cancelled':
          stats.cancelledInvoices++;
          break;
      }

      stats.totalAmount += invoice.totalAmount;
      stats.totalRegularHours += invoice.totalRegularHours;
      stats.totalOvertimeHours += invoice.totalOvertimeHours;
    });

    stats.averageInvoiceAmount = stats.totalInvoices > 0 ? 
      parseFloat((stats.totalAmount / stats.totalInvoices).toFixed(2)) : 0;

    return stats;
  }
}

export default InvoiceCalculator;
